"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useRef } from "react";
import { MapPin, CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, TrendingUp, Star, Search, Phone, Sparkles, Building2 } from 'lucide-react';
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP);
export default function Localseohero() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    if (!containerRef.current) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".hero-fade", {
        y: 24
      });
      gsap.to(".hero-fade", {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.1,
        ease: "power3.out"
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".hero-fade", {
        opacity: 1,
        y: 0
      });
    });
  }, {
    scope: containerRef
  });
  const trustPoints = [{
    icon: ShieldCheck,
    title: "Direct Consultant Access",
    desc: "You work directly with me, Yassine Essaadi. No account managers or junior delegates."
  }, {
    icon: MapPin,
    title: "Chelmsford Map Pack Focus",
    desc: "Optimised Google Business Profile and local citations to dominate Essex local search."
  }, {
    icon: TrendingUp,
    title: "Transparent & Sustainable",
    desc: "Clear deliverables, ethical SEO techniques, and honest reporting with no lock-in contracts."
  }];
  const localMetrics = [{
    label: "Local Visibility",
    value: "Top 3 Map Pack"
  }, {
    label: "Direct Access",
    value: "100% Yassine"
  }, {
    label: "Client Focus",
    value: "Chelmsford & Essex"
  }];
  return <section id="localseohero" data-nav="light" className="relative bg-background text-foreground py-12 md:py-20 lg:py-24 overflow-hidden">
      <div ref={containerRef} className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {}
        <div className="relative rounded-3xl bg-primary text-primary-foreground p-6 sm:p-10 md:p-12 lg:p-16 shadow-2xl overflow-hidden border border-primary/20">
          {}
          <div className="absolute -top-24 -right-24 size-96 rounded-full bg-accent/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 size-96 rounded-full bg-secondary/20 blur-3xl pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {}
            <div className="lg:col-span-7 space-y-6 md:space-y-8">
              {}
              <div className="hero-fade inline-flex items-center gap-2" style={{
              opacity: 0
            }}>
                <Badge variant="secondary" className="bg-secondary/90 text-secondary-foreground hover:bg-secondary border border-accent/20 px-3.5 py-1.5 text-xs sm:text-sm font-medium rounded-full shadow-xs gap-1.5">
                  <MapPin className="size-4 text-accent shrink-0" />
                  <span>Chelmsford &amp; Essex Local SEO Expert</span>
                </Badge>
              </div>

              {}
              <div className="hero-fade space-y-3" style={{
              opacity: 0
            }}>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary-foreground leading-tight">
                  Local SEO Consultant in Chelmsford
                </h2>
                <p className="text-primary-foreground/90 text-base sm:text-lg md:text-xl font-sans font-normal leading-relaxed max-w-2xl">
                  I help businesses in Chelmsford and across Essex capture high-intent local customers from Google Search and Maps. When you partner with YE Consultancy, you work directly with me — no account managers, no junior handoffs, just practical, high-converting local search growth.
                </p>
              </div>

              {}
              <div className="hero-fade grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2" style={{
              opacity: 0
            }}>
                {trustPoints.map((point, i) => {
                const IconComp = point.icon;
                return <div key={point.title} data-index={i} className="bg-primary-foreground/10 backdrop-blur-xs rounded-xl p-3.5 sm:p-4 border border-primary-foreground/15 transition-all hover:bg-primary-foreground/15">
                      <div className="flex items-center gap-2 mb-1.5">
                        <IconComp className="size-4 text-accent shrink-0" />
                        <h2 className="font-serif text-sm font-semibold text-primary-foreground leading-tight">
                          {point.title}
                        </h2>
                      </div>
                      <p className="text-xs text-primary-foreground/80 leading-normal font-sans">
                        {point.desc}
                      </p>
                    </div>;
              })}
              </div>

              {}
              <div className="hero-fade flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 pt-2" style={{
              opacity: 0
            }}>
                <Button size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold px-6 py-3 rounded-xl shadow-md gap-2 text-base h-auto cursor-pointer" asChild>
                  <Link to="/ContactPage">
                    <span>Book a Free SEO Consultation</span>
                    <ArrowRight className="size-5" />
                  </Link>
                </Button>

                <Button variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/15 bg-primary-foreground/5 font-semibold px-6 py-3 rounded-xl gap-2 text-base h-auto cursor-pointer" asChild>
                  <Link to="/CaseStudiesPage">
                    <span>Explore My Case Studies</span>
                  </Link>
                </Button>
              </div>

              {}
              <div className="hero-fade flex items-center gap-3 pt-2 text-xs sm:text-sm text-primary-foreground/80" style={{
              opacity: 0
            }}>
                <CheckCircle2 className="size-4 text-accent shrink-0" />
                <span>You work 1-on-1 with Yassine Essaadi. Tailored strategies, zero fluff.</span>
              </div>
            </div>

            {}
            <div className="lg:col-span-5 hero-fade" style={{
            opacity: 0
          }}>
              <div className="bg-card text-card-foreground rounded-2xl p-6 sm:p-8 shadow-xl border border-border relative overflow-hidden space-y-6">
                {}
                <div className="flex items-center gap-4 pb-4 border-b border-border">
                  <SafeHydrate><Avatar className="size-16 sm:size-20 border-2 border-primary shadow-sm">
                    <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_local_seo_consultant.png" alt="Yassine Essaadi - Local SEO Consultant" />
                    <AvatarFallback className="bg-muted text-foreground font-semibold text-lg">
                      YE
                    </AvatarFallback>
                  </Avatar></SafeHydrate>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <h3 className="font-serif text-lg sm:text-xl font-bold text-foreground">
                        Yassine Essaadi
                      </h3>
                      <Sparkles className="size-4 text-accent" />
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground font-medium">
                      Independent Local SEO Consultant
                    </p>
                    <div className="flex items-center gap-1 mt-1 text-xs text-primary font-medium">
                      <span className="size-2 rounded-full bg-chart-4 inline-block animate-pulse" />
                      <span>Accepting Local Chelmsford Clients</span>
                    </div>
                  </div>
                </div>

                {}
                <div className="bg-muted/60 rounded-xl p-4 border border-border/80 space-y-3">
                  <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    <span className="flex items-center gap-1">
                      <Search className="size-3.5 text-primary" /> Chelmsford Map Pack
                    </span>
                    <Badge variant="outline" className="text-[11.5px] bg-background">
                      Live Rankings
                    </Badge>
                  </div>

                  <div className="space-y-2">
                    <div className="bg-background rounded-lg p-3 border border-border shadow-2xs flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="size-6 rounded-full bg-primary/10 text-primary font-bold text-xs flex items-center justify-center shrink-0">
                          1
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-foreground">Your Local Business</p>
                          <p className="text-[11.5px] text-muted-foreground">Chelmsford, Essex ·  5.0 (48)</p>
                        </div>
                      </div>
                      <Badge className="bg-chart-4 text-white text-[11.5px] px-2 py-0.5">
                        Top Result
                      </Badge>
                    </div>

                    <div className="bg-background/50 rounded-lg p-2.5 border border-border/50 flex items-center justify-between text-xs opacity-60">
                      <div className="flex items-center gap-2.5">
                        <div className="size-5 rounded-full bg-muted text-muted-foreground font-medium text-[11.5px] flex items-center justify-center shrink-0">
                          2
                        </div>
                        <span className="text-muted-foreground text-xs">Competitor Business A</span>
                      </div>
                    </div>
                  </div>
                </div>

                {}
                <div className="grid grid-cols-3 gap-2 text-center pt-1">
                  {localMetrics.map((item, i) => <div key={item.label} data-index={i} className="bg-secondary/40 rounded-lg p-2.5 border border-secondary">
                      <p className="font-serif text-sm sm:text-base font-bold text-foreground">
                        {item.value}
                      </p>
                      <p className="text-[11.5px] sm:text-xs text-muted-foreground font-medium">
                        {item.label}
                      </p>
                    </div>)}
                </div>

                {}
                <div className="pt-1">
                  <Button variant="outline" className="w-full border-border hover:bg-accent hover:text-accent-foreground font-medium text-xs sm:text-sm gap-2 h-10 rounded-lg" asChild>
                    <a href="tel:+447490984430">
                      <Phone className="size-4 text-primary" />
                      <span>Direct Line: +44 7490984430</span>
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}